/*
 * A composicao de `/interpretacao/*`, incluindo a variante de ascendente (`rising`).
 *
 * As grades de posicao vem do PHP e entram como regiao ADOTADA, nao renderizadas aqui: sao 12
 * links internos por pagina para as irmas, e e a malha de SEO das paginas de cauda longa. No
 * `InterpretationTemplate` do Next elas eram `ssr: false` — aqui o legado e melhor que a
 * referencia, de proposito.
 *
 * As duas condicionais do PHP chegam como AUSENCIA DE FRAGMENTO, e nao como regra repetida:
 * a grade de signos nao existe para o Sol e a de casas depende do idioma. A regua que abre a
 * coluna esquerda nao e dado, e viewport: sai sempre no DOM e o desktop a esconde no CSS.
 */
import React from 'react';

import useContentAuthGate from '../hooks/useContentAuthGate';
import { IContentTemplateProps } from '../types';

import AuthSection from '../blocks/AuthSection';
import ContentCta from '../blocks/ContentCta';
import Separator from '../blocks/Separator';
import ShareBar from '../blocks/ShareBar';
import AccountBlock from '../compositions/AccountBlock';
import SkyMoment from '../compositions/SkyMoment';
import StoreAndSearch from '../compositions/StoreAndSearch';
import AdoptedRegion from '../primitives/AdoptedRegion';
import ChromeBoundary from '../primitives/ChromeBoundary';
import AuthSlot from '../slots/AuthSlot';
import ContentBody from '../slots/ContentBody';
import ContentHeader from '../slots/ContentHeader';
import CtaSlot from '../slots/CtaSlot';
import LeftSection from '../slots/LeftSection';
import RightSection from '../slots/RightSection';
import ShareSlot from '../slots/ShareSlot';

const InterpretationTemplate: React.FC<IContentTemplateProps> = ({
    data,
    head,
    body,
    adopted,
    variation
}) => {
    useContentAuthGate({ isLogged: data.isLogged, authStep: data.authStep });

    return (
        <>
            <ContentHeader content={head} />

            <CtaSlot>
                {data.cta && (
                    <ChromeBoundary region="cta">
                        <ContentCta step={data.authStep} title={data.cta.title} label={data.cta.label} />
                    </ChromeBoundary>
                )}
            </CtaSlot>

            <ContentBody content={body} />

            <ShareSlot>
                {data.share && (
                    <ChromeBoundary region="share">
                        <ShareBar label={data.share.label} hrefs={data.share.hrefs} />
                    </ChromeBoundary>
                )}
            </ShareSlot>

            <LeftSection>
                <Separator opening />

                {variation === 'rising'
                    ? (
                        <AdoptedRegion
                            className="content-block content-block--rising-positions"
                            content={adopted.risingPositions}
                        />
                    )
                    : (
                        <>
                            {adopted.planetInSigns && (
                                <AdoptedRegion
                                    className="content-block content-block--planet-positions"
                                    content={adopted.planetInSigns}
                                />
                            )}

                            {adopted.planetInHouses && (
                                <>
                                    {adopted.planetInSigns && <Separator />}

                                    <AdoptedRegion
                                        className="content-block content-block--planet-positions"
                                        content={adopted.planetInHouses}
                                    />
                                </>
                            )}
                        </>
                    )}
            </LeftSection>

            <RightSection>
                <StoreAndSearch data={data} />

                <AdoptedRegion
                    className="content-block content-block--common-positions"
                    content={adopted.signsOnAstrolink}
                />

                {adopted.userPosition && (
                    <AdoptedRegion
                        className="content-block content-block--user-position"
                        content={adopted.userPosition}
                    />
                )}

                <AccountBlock
                    data={data}
                    title={data.account.title}
                    description={data.account.description}
                />

                <SkyMoment data={data} />
            </RightSection>

            <AuthSlot hasForm={!!data.authSection}>
                {data.authSection && (
                    <ChromeBoundary region="auth">
                        <AuthSection
                            title={data.authSection.title}
                            description={data.authSection.description}
                            authStep={data.authStep}
                            maintenance={data.maintenance}
                        />
                    </ChromeBoundary>
                )}
            </AuthSlot>
        </>
    );
};

export default InterpretationTemplate;
